import { ButtonPrimary } from 'components/Button'
import { LightCard } from 'components/Card'
import Column from 'components/Column'
import CurrencyLogo from 'components/CurrencyLogo'
import Row from 'components/Row'
import ConfirmationTransactionStep, {
  ConfirmationTransactionStepProps,
} from 'components/StepLine/common/ConfirmationTransactionStep'
import { useMemo } from 'react'
import { AlertTriangle, Check } from 'react-feather'
import styled from 'styled-components'
import { ThemedText } from 'theme'

import TransactionError from '../../TransactionError'

const StyledListItem = styled(Row)`
  padding: 8px 0;
  gap: 12px;
  align-items: flex-start;

  &:not(:last-child) {
    border-bottom: 1px solid ${({ theme }) => theme.backgroundOutline};
  }
`

const CheckIcon = styled(Check)`
  color: ${({ theme }) => theme.accentSuccess};
  background: ${({ theme }) => `${theme.accentSuccess}1A`};
  padding: 3px;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  min-width: 20px;
  margin-top: 2px;
`

const Description = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.textSecondary};
  margin: 0 0 16px 0;
  line-height: 1.5;
`

const TokenDisplay = styled(Row)`
  background: ${({ theme }) => theme.cardTertiary};
  border-radius: 12px;
  padding: 12px;
  gap: 12px;
  align-items: center;
  margin-bottom: 16px;
`

const StepText = styled(ThemedText.DeprecatedBody)`
  font-size: 14px;
  font-weight: 500;
`

const StepDescription = styled(ThemedText.DeprecatedSmall)`
  color: ${({ theme }) => theme.textSecondary};
  font-size: 12px;
`

type KodiakFarmUnstakeStepProps = {
  next: () => void
  onStart: () => Promise<string | null>
  earnedAmount: any[]
  stakedPositions: any[]
  totalStakedAmount: string
  stakingToken: any
  farm: any
  error?: string
  willForfeitRewards: boolean
} & Omit<ConfirmationTransactionStepProps, 'children' | 'onConfirm'>

export default function KodiakFarmUnstakeStep({
  next,
  onStart,
  earnedAmount,
  stakedPositions,
  totalStakedAmount,
  stakingToken,
  farm,
  error,
  willForfeitRewards,
  ...rest
}: KodiakFarmUnstakeStepProps) {
  const formattedAmount = useMemo(() => {
    if (!stakingToken || !totalStakedAmount) return '0'

    try {
      const decimals = stakingToken.decimals
      const divisor = BigInt(10) ** BigInt(decimals)
      const tokens = BigInt(totalStakedAmount)

      const integerPart = tokens / divisor
      const fractionalPart = tokens % divisor

      let fractionalStr = fractionalPart.toString().padStart(decimals, '0')
      fractionalStr = fractionalStr.replace(/0+$/, '')

      if (fractionalStr.length > 0) {
        return `${integerPart}.${fractionalStr}`
      } else {
        return integerPart.toString()
      }
    } catch (e) {
      console.error('Error formatting token amount:', e)
      return totalStakedAmount
    }
  }, [stakingToken, totalStakedAmount])

  const hasPositions = useMemo(() => stakedPositions && stakedPositions.length > 0, [stakedPositions])

  const hasRewards = useMemo(
    () => earnedAmount && earnedAmount.some((item) => item.amount?.greaterThan('0')),
    [earnedAmount]
  )

  return (
    <ConfirmationTransactionStep {...rest} onConfirm={next}>
      <Column width="100%" gap="16px">
        <Description>
          {willForfeitRewards
            ? 'Due to insufficient reward tokens, emergency withdrawal will be used. You will receive your staked tokens but forfeit all pending rewards.'
            : "First, we'll withdraw your positions from Kodiak Farm. This will also claim any pending rewards."}
        </Description>

        {willForfeitRewards && hasRewards && (
          <LightCard
            style={{
              background: 'rgba(255, 193, 7, 0.1)',
              border: '1px solid rgba(255, 193, 7, 0.3)',
              padding: '12px',
            }}
          >
            <Row gap="8px" align="flex-start">
              <AlertTriangle size={18} color="#FFC107" style={{ minWidth: '18px', marginTop: '2px' }} />
              <Column gap="4px">
                <ThemedText.DeprecatedBody fontWeight={600} fontSize="14px" color="#FFC107">
                  Warning: You will lose your rewards
                </ThemedText.DeprecatedBody>
                <ThemedText.DeprecatedSmall
                  style={{
                    color: '#FFC107',
                    fontSize: '11px',
                    fontStyle: 'italic',
                    opacity: 0.8,
                    marginTop: '4px',
                  }}
                >
                  Note: Contract state may change before transaction executes. This reflects current conditions.
                </ThemedText.DeprecatedSmall>
              </Column>
            </Row>
          </LightCard>
        )}

        <LightCard>
          <ThemedText.DeprecatedSubHeader style={{ padding: '4px 0 8px', fontSize: '16px' }}>
            {willForfeitRewards ? 'Emergency Withdrawal' : 'Unstaking from Kodiak Farm'}
          </ThemedText.DeprecatedSubHeader>

          <StyledListItem>
            <CheckIcon />
            <Column gap="2px">
              <StepText>Withdraw all positions from Kodiak Farm</StepText>
              <StepDescription>{stakedPositions.length} position(s) to withdraw</StepDescription>
            </Column>
          </StyledListItem>

          {hasRewards && (
            <StyledListItem style={willForfeitRewards ? { opacity: 0.5, textDecoration: 'line-through' } : {}}>
              {willForfeitRewards ? (
                <AlertTriangle size={20} color="#FFC107" style={{ minWidth: '20px' }} />
              ) : (
                <CheckIcon />
              )}
              <Column gap="2px">
                <StepText>
                  {willForfeitRewards ? 'Pending rewards (will be forfeited)' : 'Claim pending rewards'}
                </StepText>
                <StepDescription>
                  {willForfeitRewards
                    ? 'These rewards will NOT be sent to your wallet'
                    : 'Rewards will be sent to your wallet'}
                </StepDescription>
              </Column>
            </StyledListItem>
          )}
        </LightCard>

        {hasPositions && (
          <TokenDisplay>
            <CurrencyLogo currency={stakingToken} size="24px" />
            <Column>
              <ThemedText.DeprecatedBody fontWeight={500} fontSize="16px">
                {formattedAmount} {stakingToken?.symbol}
              </ThemedText.DeprecatedBody>
              <StepDescription>To be unstaked</StepDescription>
            </Column>
          </TokenDisplay>
        )}

        <ButtonPrimary onClick={onStart} disabled={!hasPositions}>
          {hasPositions ? 'Unstake from Kodiak Farm' : 'No Positions to Unstake'}
        </ButtonPrimary>
        {error && <TransactionError error={error} />}
      </Column>
    </ConfirmationTransactionStep>
  )
}
